import { formatPrice, getProductSellingPrice } from "@utilities/functions";
import { PaymentMethodeEnum } from "@utilities/enums";
import { Sale } from "@utilities/types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useClient, useOrganisation } from "./services";
import { useCallback } from "react";

interface RGBColor {
    r: number;
    g: number;
    b: number;
}

export const useInvoiceDownloader = () => {
    const { clients } = useClient();
    const { organisation } = useOrganisation()
    const convertColorToRGB = (color: string): RGBColor => {
        // Liste de couleurs communes
        const colorMap: Record<string, RGBColor> = {
            'red': { r: 255, g: 0, b: 0 },
            'green': { r: 0, g: 128, b: 0 },
            'blue': { r: 0, g: 0, b: 255 },
            'black': { r: 0, g: 0, b: 0 },
            'white': { r: 255, g: 255, b: 255 },
            'yellow': { r: 255, g: 255, b: 0 },
            'orange': { r: 255, g: 165, b: 0 },
            'purple': { r: 128, g: 0, b: 128 },
            'pink': { r: 255, g: 192, b: 203 },
            'brown': { r: 165, g: 42, b: 42 },
            'gray': { r: 128, g: 128, b: 128 },
            'grey': { r: 128, g: 128, b: 128 },
        };

        // Si c'est une couleur nommée dans notre map
        if (colorMap[color.toLowerCase()]) {
            return colorMap[color.toLowerCase()];
        }

        // Si c'est une valeur hex (#RRGGBB)
        if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
            let hex = color.substring(1);

            // Convertir le format court (#RGB) en format long (#RRGGBB)
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }

            return {
                r: parseInt(hex.substring(0, 2), 16),
                g: parseInt(hex.substring(2, 4), 16),
                b: parseInt(hex.substring(4, 6), 16)
            };
        }

        // Par défaut, retourner noir
        return { r: 0, g: 0, b: 0 };
    };

    const downloadInvoice = useCallback((sale: Sale, printDirectly: boolean = false) => {
        const clientFound = typeof sale._clientID === 'object' && sale._clientID !== null
            ? sale._clientID
            : clients.find(c => c._id === sale._clientID);

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        let yPos = 20;
        const leftMargin = 14;
        const rightMargin = pageWidth - 14;

        doc.setFillColor(85, 136, 251);
        doc.rect(0, 0, pageWidth, 40, 'F');

        // Titre de la facture
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.text("FACTURE", leftMargin, yPos);
        doc.setFontSize(15);
        doc.text(organisation?.organisationName!, leftMargin, yPos + 10);

        // Numéro de facture et date
        doc.setFontSize(10);
        const invoiceNumber = `N°: ${sale.orderNumber}`;
        const invoiceDate = `Date: ${new Date(sale.createAt).toLocaleDateString()}`;
        doc.text(invoiceNumber, rightMargin, yPos - 5, { align: 'right' });
        doc.text(invoiceDate, rightMargin, yPos + 5, { align: 'right' });

        yPos = 50;

        // Informations du client si disponible
        if (clientFound) {
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("CLIENT", leftMargin, yPos);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(12);
            yPos += 10;
            doc.text(`${clientFound?.firstname} ${clientFound.lastname}`, leftMargin, yPos);
            yPos += 6;
            if (clientFound.phoneNumber) {
                doc.text(`Tél: ${clientFound.phoneNumber}`, leftMargin, yPos);
                yPos += 6;
            }
            if (clientFound.email) {
                doc.text(`Email: ${clientFound.email}`, leftMargin, yPos);
                yPos += 6;
            }
            // Points de fidélité si disponibles
            if (clientFound.points && clientFound.points > 0) {
                doc.setTextColor(41, 128, 185);
                doc.text(`Points fidélité: ${clientFound.points}`, leftMargin, yPos);
                doc.setTextColor(0, 0, 0);
            }
            yPos += 10;
        } else {
            yPos += 5;
        }

        // Informations de paiement
        // Dessin du fond clair uniquement pour le titre
        doc.setFillColor(245, 245, 245); // Gris très léger
        const boxHeight = 25;
        doc.rect(rightMargin - 80, yPos - 7, 80, boxHeight, 'F');

        // Titre "DÉTAILS DU PAIEMENT"
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("DÉTAILS DU PAIEMENT", rightMargin - 75, yPos);

        // Infos de paiement
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        yPos += 8;
        doc.text(`Mode de paiement: ${sale?.paymentMethode?.methode}`, rightMargin - 75, yPos);

        if (sale?.paymentMethode?.methode === PaymentMethodeEnum.mobilMoney) {
            yPos += 6;
            doc.text(`Opérateur: ${sale.paymentMethode.mobilePayment}`, rightMargin - 75, yPos);
        }

        yPos += 16;

        // Ligne de séparation
        doc.setDrawColor(200, 200, 200);
        doc.line(leftMargin, yPos, rightMargin, yPos);

        yPos += 10;

        // Titre de la section des articles
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("ARTICLES", leftMargin, yPos);

        // Tableau des articles
        const tableColumn = ["Article", "Qté", "Prix unitaire", "Total"];
        const tableRows: any[] = [];

        // Stockage des informations de couleur pour traitement après génération du tableau
        let colorInfos: { itemName: string, colorValue: string, rowIndex: number }[] = [];
        let currentRowIndex = 0;

        sale.basket.forEach((item) => {
            const price = getProductSellingPrice(item.product);
            const totalItemPrice = price * item.quantity;

            // Ligne principale du produit
            tableRows.push([
                item.product.name,
                item.quantity,
                formatPrice(price),
                formatPrice(totalItemPrice)
            ]);
            currentRowIndex++;

            // Gestion des couleurs - ligne séparée
            if (item.selectedColor) {
                // Pour une seule couleur
                colorInfos.push({
                    itemName: item.product.name,
                    colorValue: item.selectedColor,
                    rowIndex: currentRowIndex
                });

                tableRows.push([
                    {
                        content: "Couleur: ",
                        colSpan: 4,
                        styles: {
                            fontStyle: 'italic',
                            fontSize: 8,
                            textColor: [100, 100, 100]
                        }
                    }
                ]);
                currentRowIndex++;
            } else if (item.selectedColor && Array.isArray(item.selectedColor) && item.selectedColor.length > 0) {
                // Pour plusieurs couleurs
                tableRows.push([
                    {
                        content: "Couleurs: ",
                        colSpan: 4,
                        styles: {
                            fontStyle: 'italic',
                            fontSize: 8,
                            textColor: [100, 100, 100]
                        }
                    }
                ]);
                currentRowIndex++;

                // Enregistrer chaque couleur pour le dessin ultérieur
                item.selectedColor.split(',').forEach(color => {
                    colorInfos.push({
                        itemName: item.product.name,
                        colorValue: color,
                        rowIndex: currentRowIndex - 1
                    });
                });
            }

            // Gestion des tailles - ligne séparée
            if (item.selectedSize) {
                // Pour une seule taille
                tableRows.push([
                    {
                        content: `Taille: ${item.selectedSize}`,
                        colSpan: 4,
                        styles: {
                            fontStyle: 'italic',
                            fontSize: 8,
                            textColor: [100, 100, 100]
                        }
                    }
                ]);
                currentRowIndex++;
            } else if (item.selectedSize && Array.isArray(item.selectedSize) && item.selectedSize.length > 0) {
                // Pour plusieurs tailles
                tableRows.push([
                    {
                        content: `Taille: ${item.selectedSize}`,
                        colSpan: 4,
                        styles: {
                            fontStyle: 'italic',
                            fontSize: 8,
                            textColor: [100, 100, 100]
                        }
                    }
                ]);
                currentRowIndex++;
            }
        });

        // Génération du tableau
        autoTable(doc, {
            startY: yPos + 5,
            head: [tableColumn],
            body: tableRows,
            theme: "grid",
            headStyles: {
                fillColor: [85, 136, 251],
                textColor: [255, 255, 255],
                fontSize: 10,
                fontStyle: "bold"
            },
            bodyStyles: {
                fontSize: 9
            },
            alternateRowStyles: {
                fillColor: [245, 250, 255]
            },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 20, halign: 'center' },
                2: { cellWidth: 40, halign: 'right' },
                3: { cellWidth: 40, halign: 'right' }
            },
            styles: {
                cellPadding: 5,
                lineWidth: 0.1,
                lineColor: [220, 220, 220]
            },
            margin: { left: leftMargin, right: rightMargin },
            didDrawCell: (data) => {
                // Après avoir dessiné chaque cellule, vérifions si nous devons dessiner un cercle de couleur
                if (data.section === 'body' && data.column.index === 0) {
                    const rowIndex = data.row.index;

                    // Gestion des cellules contenant des couleurs
                    const cellText = data.cell.text.join('');

                    if (cellText.includes('Couleur:')) {
                        // Récupérer la couleur pour cette ligne
                        const colorInfo = colorInfos.find(info => info.rowIndex === rowIndex);

                        if (colorInfo) {
                            // Calculer la position exacte pour le cercle coloré
                            const colorText = "Couleur: ";
                            const colorTextWidth = doc.getTextWidth(colorText);

                            // Position X du cercle (juste après "Couleur: ")
                            const circleX = data.cell.x + data.cell.padding('left') + colorTextWidth + 3;
                            // Centre vertical de la cellule
                            const circleY = data.cell.y + data.cell.height / 2;

                            // Convertir la valeur de couleur en RGB
                            const rgbColor = convertColorToRGB(colorInfo.colorValue);

                            // Sauvegarder l'état actuel
                            doc.saveGraphicsState();

                            // Dessiner le cercle
                            doc.setFillColor(rgbColor.r, rgbColor.g, rgbColor.b);
                            doc.circle(circleX, circleY, 1.5, 'F'); // Cercle de 1.5mm de rayon

                            // Ajouter une bordure noire au cercle pour les couleurs claires
                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(0.2);
                            doc.circle(circleX, circleY, 1.5, 'S');

                            // Restaurer l'état précédent
                            doc.restoreGraphicsState();
                        }
                    } else if (cellText.includes('Couleurs:')) {
                        // Récupérer toutes les couleurs pour cette ligne
                        const allColorsForThisRow = colorInfos.filter(info => info.rowIndex === rowIndex);

                        // Calculer la position de départ
                        const colorsText = "Couleurs: ";
                        const colorsTextWidth = doc.getTextWidth(colorsText);
                        let startX = data.cell.x + data.cell.padding('left') + colorsTextWidth + 3;

                        // Dessiner tous les cercles
                        allColorsForThisRow.forEach((colorInfo, index) => {
                            const circleX = startX + (index * 8); // 8mm d'espacement entre les cercles
                            const circleY = data.cell.y + data.cell.height / 2;

                            const rgbColor = convertColorToRGB(colorInfo.colorValue);

                            doc.saveGraphicsState();
                            doc.setFillColor(rgbColor.r, rgbColor.g, rgbColor.b);
                            doc.circle(circleX, circleY, 1.5, 'F');
                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(0.2);
                            doc.circle(circleX, circleY, 1.5, 'S');
                            doc.restoreGraphicsState();
                        });
                    }
                }
            }
        });

        // Récupération de la position finale du tableau
        let finalY = (doc as any).lastAutoTable.finalY + 10;

        // Section totaux
        const totalBoxHeight = 30;
        const totalBoxWidth = 100;

        doc.setDrawColor(220, 220, 220);
        doc.setFillColor(230, 230, 230); // Blanc ou très clair
        doc.rect(rightMargin - totalBoxWidth, finalY, totalBoxWidth, totalBoxHeight, 'FD');

        // Texte dans la boîte
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0); // S'assurer que le texte est bien noir
        doc.text("Sous-total:", rightMargin - 90, finalY + 10);
        doc.text(formatPrice(sale.totalToPay), rightMargin - 10, finalY + 10, { align: 'right' });

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Total payé:", rightMargin - 90, finalY + 22);
        doc.text(formatPrice(sale.totalToPay), rightMargin - 10, finalY + 22, { align: 'right' });

        // Pied de page
        finalY = doc.internal.pageSize.getHeight() - 5;
        doc.setDrawColor(85, 136, 251);
        doc.setLineWidth(0.3);
        doc.line(leftMargin, finalY - 5, rightMargin, finalY - 5);

        doc.setTextColor(100, 100, 100);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text("Merci pour votre achat !", pageWidth / 2, finalY, { align: 'center' });

        // Génération du PDF
        const pdfData = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfData);

        if (printDirectly) {
            // Option 1: Impression directe via iframe caché
            const printIframe = document.createElement('iframe');
            printIframe.style.position = 'absolute';
            printIframe.style.top = '-9999px';
            printIframe.style.left = '-9999px';
            document.body.appendChild(printIframe);

            printIframe.onload = () => {
                setTimeout(() => {
                    printIframe.contentWindow?.focus();
                    printIframe.contentWindow?.print();

                    // Après impression ou annulation, supprimer l'iframe
                    setTimeout(() => {
                        document.body.removeChild(printIframe);
                        URL.revokeObjectURL(pdfUrl);
                    }, 10000); // à revoir si besoin
                }, 500);
            };

            printIframe.src = pdfUrl;
        } else {
            // Comportement standard: ouvrir dans un nouvel onglet
            window.open(pdfUrl, '_blank');
        }
    }, [clients]);

    return { downloadInvoice };
};
